import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import BaseLoader from "../../../components/ui/BaseLoader";
import {
  addEngineerInvoiceWorkOrders,
  addInvoiceWorkOrders,
  fetchAvailableWorkOrdersForInvoice,
} from "../../../services/api/invoice";
import { showToast } from "../../../store/toast/toastSlice";
import { UserType } from "../../../utils/enum";
import { formatTwoDecimals } from "../../../utils/helpers";

/**
 * Picker for adding approved work orders to an existing invoice. The list comes
 * from the backend already filtered to what the add endpoint will accept, so
 * everything shown here is addable.
 */
const AddWorkOrderToInvoiceModal = ({ invoice, role, open, close, onAdded }) => {
  const dispatch = useDispatch();
  const [selectedIds, setSelectedIds] = useState([]);

  const isClientInvoice = role === UserType.CLIENT;

  const {
    isPending: isAvailableWorkOrdersLoading,
    data: availableWorkOrders,
    refetch: refetchAvailableWorkOrders,
  } = useQuery({
    queryKey: ["availableInvoiceWorkOrders", invoice?.id],
    queryFn: () => fetchAvailableWorkOrdersForInvoice(invoice?.id),
    // Only fetched while the modal is open, so reopening always shows a list
    // that reflects any additions or removals made in between.
    enabled: !!invoice?.id && open,
  });

  const { isPending: isAddWorkOrdersLoading, mutateAsync: addWorkOrdersMutation } = useMutation({
    mutationFn: isClientInvoice ? addInvoiceWorkOrders : addEngineerInvoiceWorkOrders,
  });

  // Clearing on close stops a previous selection reappearing next time, and
  // drops ids that may no longer be eligible.
  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
    }
  }, [open]);

  const workOrders = availableWorkOrders?.data || [];

  // Declared above their first use: selectableWorkOrders below runs during
  // render, so a const defined later would still be in its temporal dead zone.
  const grandTotalFor = (workOrder) =>
    isClientInvoice ? workOrder?.payment?.clientGrandTotal : workOrder?.payment?.engineerGrandTotal;

  const currencyFor = (workOrder) =>
    isClientInvoice ? workOrder?.payment?.clientCurrency : workOrder?.payment?.engineerCurrency;

  const locationFor = (workOrder) =>
    [workOrder?.address?.displayName, workOrder?.address?.city, workOrder?.address?.country]
      .filter(Boolean)
      .join(", ");

  // An invoice carries one currency and its totals are a plain sum, so a work
  // order priced in another currency cannot go on it — the backend rejects it
  // too, this just says so before the user submits.
  const isCurrencyMismatched = (workOrder) => {
    const workOrderCurrency = currencyFor(workOrder);
    return Boolean(workOrderCurrency && invoice?.currency && workOrderCurrency !== invoice.currency);
  };

  const selectableWorkOrders = workOrders.filter((workOrder) => !isCurrencyMismatched(workOrder));

  const toggleWorkOrder = (workOrder) => {
    if (isCurrencyMismatched(workOrder)) {
      dispatch(
        showToast({
          type: "error",
          message: `Work order ${workOrder.referenceId} is priced in ${currencyFor(
            workOrder,
          )} and cannot be added to this ${invoice?.currency} invoice.`,
        }),
      );
      return;
    }

    setSelectedIds((current) =>
      current.includes(workOrder.id)
        ? current.filter((id) => id !== workOrder.id)
        : [...current, workOrder.id],
    );
  };

  // Select-all covers only what can actually be added, so it never puts the
  // user in a state the Add button would reject.
  const toggleAll = () => {
    setSelectedIds((current) =>
      current.length === selectableWorkOrders.length
        ? []
        : selectableWorkOrders.map((workOrder) => workOrder.id),
    );
  };

  const handleAddWorkOrders = async () => {
    try {
      await addWorkOrdersMutation({
        invoiceId: invoice.id,
        workOrderIds: selectedIds,
      });

      dispatch(
        showToast({
          type: "success",
          message: `${selectedIds.length} work order${
            selectedIds.length === 1 ? "" : "s"
          } added to Invoice INV-#${invoice.id} successfully`,
        }),
      );

      close(false);
      await onAdded();
    } catch (error) {
      // The add endpoint rejects the whole batch on the first bad work order and
      // names it, so the server message is more useful than a generic one.
      dispatch(
        showToast({
          type: "error",
          message: error?.response?.data?.message || "Failed to add work orders",
        }),
      );
      // A rejection usually means the list is stale — another user invoiced one
      // of these in the meantime.
      await refetchAvailableWorkOrders();
    }
  };

  return (
    <BaseModal className="-top-[200px] overflow-auto" open={open} close={() => close(false)}>
      {/* Fluid rather than the stepped breakpoint widths the other invoice
          modals use: this one holds a table, so it should take whatever the
          viewport allows and stop at 1200px rather than jumping between fixed
          sizes. Narrower gutter too (px-6, not px-12) — those 96px came
          straight out of the columns. */}
      <div className="py-7 px-6 ">
        <h6 className="text-dark font-normal border-b pb-1">
          Add Work Order to Invoice INV-#{invoice?.id}
        </h6>

        {isAvailableWorkOrdersLoading ? (
          <div className="flex justify-center py-16">
            <BaseLoader />
          </div>
        ) : workOrders.length === 0 ? (
          <p className="text-center text-[13px] text-gray-500 py-16">
            No approved work orders are available to add to this invoice.
          </p>
        ) : (
          <div className="mt-4 max-h-[420px] overflow-auto border rounded-md">
            {/* Every real column has an exact px width (48+96+128+400+144 =
                816px) and table-fixed holds them to it. The trailing spacer
                column takes any leftover, so a wider modal grows the empty gap
                on the right rather than stretching a column. w-full is kept
                only so the header band and row hover span the full width; the
                min-w matches the column total, so below that the wrapper
                scrolls instead of squeezing them. */}
            {/* The leading `table` class is load-bearing: index.css has a global
                `table { display: grid !important }` rule, which drops real table
                layout and makes column widths and header/body alignment do
                nothing. Tailwind is configured with important:"body", so this
                emits `body .table{display:table!important}` — higher specificity
                than the bare element rule, so it wins it back. */}
            <table className="table w-full table-fixed text-[13px]">
              <thead>
                {/* sticky sits on the th, not the thead: position:sticky on
                    thead is ignored by Safari and older Chrome, so the header
                    would scroll away with the rows. */}
                <tr className="[&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-light [&>th]:border-b">
                  {/* text-left is explicit: th defaults to centred, which put
                      this checkbox out of line with the row checkboxes below. */}
                  <th className="w-12 px-2 py-2 text-left">
                    <BaseCheckbox
                      checked={
                        selectableWorkOrders.length > 0 &&
                        selectedIds.length === selectableWorkOrders.length
                      }
                      disabled={selectableWorkOrders.length === 0}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="w-24 text-left px-2 py-2 font-normal">WO #</th>
                  <th className="w-32 text-left px-2 py-2 font-normal">Date</th>
                  <th className="w-[300px] text-left px-2 py-2 font-normal">Work Order Details</th>
                  <th className="w-36 text-right px-2 py-2 font-normal">Amount</th>
                  {/* Deliberately unlabelled — the cells below read "View",
                      which names the action on its own. */}
                  <th aria-hidden="true" className="w-20 px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {workOrders.map((workOrder) => (
                  <tr
                    key={workOrder.id}
                    // Mismatched rows stay visible and clickable rather than
                    // being hidden: clicking one explains why it cannot be
                    // added, which is more useful than it silently not existing.
                    className={`border-t cursor-pointer hover:bg-light ${
                      isCurrencyMismatched(workOrder) ? "opacity-60" : ""
                    }`}
                    onClick={() => toggleWorkOrder(workOrder)}
                  >
                    {/* The row is clickable too, so without this the checkbox
                        would toggle twice — once itself, once via the row — and
                        appear not to respond at all. */}
                    <td
                      className="w-12 px-2 py-2 align-middle"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <BaseCheckbox
                        checked={selectedIds.includes(workOrder.id)}
                        disabled={isCurrencyMismatched(workOrder)}
                        onChange={() => toggleWorkOrder(workOrder)}
                      />
                    </td>
                    <td className="w-24 px-2 py-2 align-middle whitespace-nowrap">
                      {workOrder.referenceId}
                    </td>
                    <td className="w-32 px-2 py-2 align-middle whitespace-nowrap">
                      {workOrder.orderStartDate
                        ? dayjs(workOrder.orderStartDate).format("DD-MMM-YYYY")
                        : "-"}
                    </td>
                    {/* Location sits under the title rather than in its own
                        column — as a column it took 224px that Details needed
                        more. Both lines truncate with the full value on hover,
                        since a fixed column cannot grow and wrapping would give
                        every row a different height. */}
                    <td className="w-[300px] px-2 py-2 align-middle">
                      <p className="truncate" title={workOrder.title || workOrder.incidentId || ""}>
                        {workOrder.title || workOrder.incidentId || "-"}
                      </p>
                      {locationFor(workOrder) && (
                        <p
                          className="truncate text-[11px] text-gray-500"
                          title={locationFor(workOrder)}
                        >
                          {locationFor(workOrder)}
                        </p>
                      )}
                    </td>
                    {/* A mismatched currency is coloured rather than only
                        explained on click, so the reason a row cannot be picked
                        is visible before the user tries. */}
                    <td
                      className={`w-36 px-2 py-2 align-middle text-right whitespace-nowrap ${
                        isCurrencyMismatched(workOrder) ? "text-red-600" : ""
                      }`}
                      title={
                        isCurrencyMismatched(workOrder)
                          ? `This invoice is in ${invoice?.currency}`
                          : undefined
                      }
                    >
                      {grandTotalFor(workOrder) != null
                        ? `${formatTwoDecimals(grandTotalFor(workOrder))} ${
                            currencyFor(workOrder) || ""
                          }`
                        : "-"}
                    </td>
                    {/* Opens in a new tab rather than navigating: this is a
                        modal, so routing away would close it and throw away
                        whatever the user had already ticked. stopPropagation
                        keeps the click off the row's select handler. */}
                    <td
                      className="w-20 px-2 py-2 align-middle text-center"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <a
                        // Client (customer) invoices open the work order in its
                        // customer-board variant, matching how EditInvoiceTable
                        // links out of the invoice it belongs to.
                        href={`/workOrder/details/${workOrder.id}${
                          isClientInvoice ? "?fromCustomerBoard=true" : ""
                        }`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`View work order ${workOrder.referenceId}`}
                        aria-label={`View work order ${workOrder.referenceId} in a new tab`}
                        className="text-primary hover:underline"
                      >
                        View
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{ marginRight: "16px" }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          isLoading={isAddWorkOrdersLoading}
          disabled={selectedIds.length === 0}
          loaderSize={24}
          loaderColor="primary"
          className="px-[40px] py-[5px]"
          color="primary"
          onClick={handleAddWorkOrders}
        >
          {selectedIds.length > 0 ? `Add ${selectedIds.length}` : "Add"}
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default AddWorkOrderToInvoiceModal;
